"use client";

import { IProduct } from "@/types/product-d-t";
import { useState, useEffect } from "react";
import ProductItem from "./single-product/product-item";

type IProps = {
  products?: IProduct[];
  spacing?: string;
  style_2?: boolean;
  featuredProducts?: IProduct[];
};

const SaleOffProducts = ({
  products = [],
  spacing = "pb-100",
  style_2 = false,
  featuredProducts = [],
}: IProps) => {
  const [visibleCount, setVisibleCount] = useState<number>(8);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSeeMore = () => {
    const total = featuredProducts.length || products.length;
    setVisibleCount(total);
  };

  const productsToShow =
    (featuredProducts.length ? featuredProducts : products).slice(
      0,
      visibleCount
    );

  return (
    <>
      <section className={`sale__area themed-sale-section ${spacing}`}>
        <div className="container">
          {/* TITLE */}
          <div className="row">
            <div className="col-12 text-center mb-50">
              {/* SPARKLING HEARTS */}
              <div className="hearts-wrapper">
                <span className="heart-icon heart-1">💖</span>
                <span className="heart-icon heart-2">💝</span>
                <span className="heart-icon heart-3">💗</span>
                <span className="heart-icon heart-4">💕</span>
                <span className="heart-icon heart-5">💓</span>
              </div>

              <h2 className="animated-title">
                Gifts That Make Hearts Happy
              </h2>

              <p className="animated-subtitle">
                Every gift tells a story. Find the perfect present that brings smiles, creates memories & shows you care
              </p>
            </div>
          </div>

          {/* PRODUCT GRID */}
          <div className="row g-3 product-grid-wrapper">
            {productsToShow.map((product: any, i: number) => {
              const firstImage =
                product.images?.[0]?.image || "/placeholder.png";
              const normalizedProduct = { ...product, image: firstImage };

              return (
                <div
                  key={i + product?.id}
                  className={isMobile ? "col-6" : "col-xl-3 col-lg-3 col-md-6 col-sm-6"}
                >
                  <ProductItem product={normalizedProduct} />
                </div>
              );
            })}
          </div>

          {/* SEE MORE BUTTON */}
          {visibleCount <
            (featuredProducts.length || products.length) && (
            <div className="row mt-30">
              <div className="col-12 text-center">
                <button
                  onClick={handleSeeMore}
                  className="see-more-btn-theme"
                >
                  <span className="btn-icon">💝</span>
                  <span>Discover More Gifts</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===== THEME CSS ===== */}
        <style jsx>{`
          .themed-sale-section {
            padding-top: 80px;
            padding-bottom: 90px;
            background: linear-gradient(135deg, #FFF0F5 0%, #FFF5F9 50%, #FFFAF0 100%);
            position: relative;
            overflow: hidden;
            margin-top: -55px;
          }

          /* DECORATIVE HEART BACKGROUND */
          .themed-sale-section::before {
            content: "💝";
            position: absolute;
            top: 80px;
            left: 5%;
            font-size: 180px;
            opacity: 0.04;
            pointer-events: none;
            animation: heartbeat 4s ease-in-out infinite;
          }

          .themed-sale-section::after {
            content: "💗";
            position: absolute;
            bottom: 120px;
            right: 8%;
            font-size: 150px;
            opacity: 0.04;
            pointer-events: none;
            animation: heartbeat 5s ease-in-out infinite reverse;
          }

          @keyframes heartbeat {
            0%, 100% {
              transform: scale(1);
              opacity: 0.04;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.06;
            }
          }

          /* FLOATING HEARTS */
          .hearts-wrapper {
            position: relative;
            height: 60px;
            margin-bottom: 15px;
          }

          .heart-icon {
            position: absolute;
            font-size: 30px;
            opacity: 0;
            animation: floatHeart 3.5s ease-in-out infinite;
            left: 50%;
            top: 0;
            filter: drop-shadow(0 4px 10px rgba(255,105,180,0.3));
          }

          .heart-1 {
            transform: translateX(-140px);
            animation-delay: 0s;
          }

          .heart-2 {
            transform: translateX(110px);
            animation-delay: 0.7s;
          }

          .heart-3 {
            transform: translateX(-70px);
            animation-delay: 1.4s;
          }

          .heart-4 {
            transform: translateX(40px);
            animation-delay: 2.1s;
          }

          .heart-5 {
            transform: translateX(-200px);
            animation-delay: 2.8s;
          }

          @keyframes floatHeart {
            0%, 100% {
              opacity: 0;
              transform: translateX(var(--x, 0)) translateY(0) scale(0.7) rotate(-15deg);
            }
            15% {
              opacity: 1;
            }
            50% {
              opacity: 1;
              transform: translateX(var(--x, 0)) translateY(-40px) scale(1.2) rotate(15deg);
            }
            85% {
              opacity: 1;
            }
          }

          .animated-title {
            font-size: 48px;
            font-weight: 900;
            background: linear-gradient(135deg, #FF1493 0%, #FF69B4 30%, #FF6B9D 70%, #FF1493 100%);
            background-size: 200% 200%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: gradientShift 5s ease infinite;
            margin-bottom: 18px;
            display: inline-block;
            letter-spacing: -0.5px;
            filter: drop-shadow(0 2px 6px rgba(255,105,180,0.15));
          }

          .animated-subtitle {
            font-size: 18px;
            color: #555;
            max-width: 700px;
            margin: 0 auto;
            line-height: 1.8;
            font-weight: 500;
          }

          /* ================= ANIMATIONS ================= */
          @keyframes gradientShift {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }

          /* PRODUCT GRID - MOBILE FIX */
          .product-grid-wrapper {
            margin: 0 auto;
            max-width: 100%;
          }

          @media (max-width: 767px) {
            .themed-sale-section {
              padding-top: 60px;
              padding-bottom: 70px;
              padding-left: 0;
              padding-right: 0;
            }

            .container {
              padding-left: 15px;
              padding-right: 15px;
              max-width: 100%;
            }

            .product-grid-wrapper {
              margin-left: 0;
              margin-right: 0;
              padding: 0;
            }

            .product-grid-wrapper > [class*="col-"] {
              padding-left: 6px;
              padding-right: 6px;
            }

            .hearts-wrapper {
              height: 45px;
              margin-bottom: 10px;
            }

            .heart-icon {
              font-size: 22px;
            }

            .animated-title {
              font-size: 30px;
            }

            .animated-subtitle {
              font-size: 15px;
              max-width: 92%;
              margin: 0 auto;
              padding: 0 5px;
            }

            .themed-sale-section::before {
              font-size: 100px;
              top: 50px;
              left: -10px;
            }

            .themed-sale-section::after {
              font-size: 90px;
              bottom: 80px;
              right: -10px;
            }
          }

          .see-more-btn-theme {
            padding: 17px 55px;
            border-radius: 50px;
            border: none;
            color: #ffffff;
            font-size: 16px;
            font-weight: 800;
            margin-top: 40px;
            background: linear-gradient(135deg, #FF1493 0%, #FF69B4 100%);
            cursor: pointer;
            transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 
              0 5px 15px rgba(255,20,147,0.35),
              0 10px 30px rgba(255,105,180,0.25);
            letter-spacing: 0.5px;
            display: inline-flex;
            align-items: center;
            gap: 10px;
            position: relative;
            overflow: hidden;
          }

          .see-more-btn-theme::before {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255,255,255,0.35);
            transform: translate(-50%, -50%);
            transition: width 0.6s ease, height 0.6s ease;
          }

          .see-more-btn-theme:hover::before {
            width: 320px;
            height: 320px;
          }

          .see-more-btn-theme:hover {
            transform: translateY(-4px) scale(1.02);
            box-shadow: 
              0 8px 20px rgba(255,20,147,0.4),
              0 15px 40px rgba(255,105,180,0.3);
          }

          .btn-icon {
            font-size: 20px;
            position: relative;
            z-index: 1;
            animation: heartPulse 1.8s ease-in-out infinite;
          }

          @keyframes heartPulse {
            0%, 100% {
              transform: scale(1);
            }
            30% {
              transform: scale(1.15);
            }
            60% {
              transform: scale(0.95);
            }
          }

          .see-more-btn-theme span {
            position: relative;
            z-index: 1;
          }

          /* MOBILE BUTTON ADJUST */
          @media (max-width: 767px) {
            .see-more-btn-theme {
              padding: 14px 42px;
              font-size: 14px;
              margin-top: 30px;
            }

            .btn-icon {
              font-size: 18px;
            }
          }
        `}</style>
      </section>
    </>
  );
};

export default SaleOffProducts;